/* ===========================================================
   Atelier de Luxe — Trade Account Dashboard
   Tabs + Project wishlists (storage + UI + bulk-add-to-cart)
   -----------------------------------------------------------
   Storage note (V1): Project wishlists are persisted in
   localStorage keyed by customer ID. The trade.wishlists
   customer metafield exists in Shopify and can be populated
   later via Shopify Flow or a small custom app — at that
   point this script can be upgraded to read/write via the
   Customer Account API. Local persistence works immediately
   on Basic plan with no extra apps.
   =========================================================== */

(function () {
  'use strict';

  const root = document.querySelector('[data-trade-dashboard]');
  if (!root) return;

  const customerId = root.getAttribute('data-customer-id') || 'anon';
  const STORAGE_KEY = 'adl_trade_wishlists_' + customerId;

  /* ---------- Storage ---------- */
  function load() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return { projects: [] };
      const parsed = JSON.parse(raw);
      if (!parsed || !Array.isArray(parsed.projects)) return { projects: [] };
      return parsed;
    } catch (e) {
      console.warn('[trade] Failed to load wishlists', e);
      return { projects: [] };
    }
  }
  function save(state) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
      console.warn('[trade] Failed to save wishlists', e);
    }
  }

  /* ---------- Toast ---------- */
  let toastEl = null;
  let toastTimer = null;
  function toast(msg) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'trade-toast';
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = msg;
    toastEl.setAttribute('data-visible', 'true');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => {
      toastEl.setAttribute('data-visible', 'false');
    }, 2400);
  }

  /* ---------- Tabs ---------- */
  function initTabs() {
    const tabs = root.querySelectorAll('[role="tab"]');
    const panels = root.querySelectorAll('[role="tabpanel"]');
    if (!tabs.length) return;

    function activate(target) {
      tabs.forEach((t) => {
        const on = t.getAttribute('data-tab') === target;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
      });
      panels.forEach((p) => {
        const on = p.getAttribute('data-panel') === target;
        p.setAttribute('aria-hidden', on ? 'false' : 'true');
      });
      try {
        const url = new URL(window.location.href);
        url.hash = target;
        history.replaceState(null, '', url.toString());
      } catch (e) { /* noop */ }
    }

    tabs.forEach((t) => {
      t.addEventListener('click', () => activate(t.getAttribute('data-tab')));
      t.addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
        const arr = Array.from(tabs);
        const idx = arr.indexOf(t);
        const next = e.key === 'ArrowRight'
          ? arr[(idx + 1) % arr.length]
          : arr[(idx - 1 + arr.length) % arr.length];
        next.focus();
        activate(next.getAttribute('data-tab'));
      });
    });

    const hash = (window.location.hash || '').replace('#', '');
    if (hash && root.querySelector('[data-tab="' + hash + '"]')) {
      activate(hash);
    }
  }

  /* ---------- Projects render ---------- */
  const listEl = root.querySelector('[data-projects-list]');
  const emptyEl = root.querySelector('[data-projects-empty]');
  const createForm = root.querySelector('[data-projects-create]');
  const createInput = createForm ? createForm.querySelector('input[name="project-name"]') : null;

  function escapeHTML(s) {
    return String(s || '').replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
  }

  function renderProjects() {
    if (!listEl) return;
    const state = load();
    if (!state.projects.length) {
      listEl.innerHTML = '';
      if (emptyEl) emptyEl.style.display = '';
      return;
    }
    if (emptyEl) emptyEl.style.display = 'none';

    listEl.innerHTML = state.projects.map((p, i) => {
      const itemCount = (p.items || []).reduce((n, it) => n + (it.qty || 1), 0);
      const itemsHTML = (p.items || []).map((it, j) => `
        <li class="trade-project__item" data-item-index="${j}">
          ${it.image ? `<img src="${escapeHTML(it.image)}" alt="">` : '<div></div>'}
          <p class="trade-project__item-title">
            ${it.url ? `<a href="${escapeHTML(it.url)}">${escapeHTML(it.title || 'Product')}</a>` : escapeHTML(it.title || 'Product')}
            ${it.variant_title && it.variant_title !== 'Default Title' ? `<br><span style="font-size:12px;color:var(--trade-warm-soft);letter-spacing:.08em;text-transform:uppercase;">${escapeHTML(it.variant_title)}</span>` : ''}
          </p>
          <span class="trade-project__item-qty">×${it.qty || 1}</span>
          <button type="button" class="trade-project__item-remove" data-remove-item="${j}" aria-label="Remove item">Remove</button>
        </li>
      `).join('');

      return `
        <article class="trade-project" data-project-index="${i}" data-expanded="false">
          <header class="trade-project__header" data-project-toggle>
            <h3 class="trade-project__name">${escapeHTML(p.name)}</h3>
            <span class="trade-project__meta">${itemCount} item${itemCount === 1 ? '' : 's'}</span>
            <span class="trade-project__caret" aria-hidden="true"></span>
          </header>
          <div class="trade-project__body">
            ${(p.items || []).length
              ? `<ul class="trade-project__items">${itemsHTML}</ul>`
              : '<p style="font-family:Josefin Slab,Georgia,serif;font-size:14px;color:var(--trade-warm-soft);margin:24px 0;font-style:italic;">No items yet. Add products from a product page using the "Add to project" button.</p>'}
            <div class="trade-project__actions">
              <button type="button" class="trade-btn trade-btn--small" data-add-to-cart ${(!p.items || !p.items.length) ? 'disabled style="opacity:.4;cursor:not-allowed;"' : ''}>Add project to cart</button>
              <button type="button" class="trade-btn trade-btn--small trade-btn--ghost" data-rename>Rename</button>
              <button type="button" class="trade-btn trade-btn--small trade-btn--ghost" data-delete>Delete project</button>
            </div>
          </div>
        </article>
      `;
    }).join('');
  }

  function refreshProjectSelects() {
    document.dispatchEvent(new CustomEvent('trade:projects-changed'));
  }

  if (createForm) {
    createForm.addEventListener('submit', (e) => {
      e.preventDefault();
      const name = (createInput.value || '').trim();
      if (!name) return;
      const state = load();
      if (state.projects.some((p) => p.name.toLowerCase() === name.toLowerCase())) {
        toast('A project with that name already exists');
        return;
      }
      state.projects.push({ name: name, items: [] });
      save(state);
      createInput.value = '';
      renderProjects();
      refreshProjectSelects();
      toast('Project created');
    });
  }

  if (listEl) {
    listEl.addEventListener('click', (e) => {
      const projectEl = e.target.closest('.trade-project');
      if (!projectEl) return;
      const idx = parseInt(projectEl.getAttribute('data-project-index'), 10);
      const state = load();
      const project = state.projects[idx];
      if (!project) return;

      if (e.target.closest('[data-project-toggle]')) {
        const expanded = projectEl.getAttribute('data-expanded') === 'true';
        projectEl.setAttribute('data-expanded', expanded ? 'false' : 'true');
        return;
      }
      if (e.target.matches('[data-remove-item]')) {
        const j = parseInt(e.target.getAttribute('data-remove-item'), 10);
        project.items.splice(j, 1);
        save(state);
        renderProjects();
        refreshProjectSelects();
        return;
      }
      if (e.target.matches('[data-rename]')) {
        const next = prompt('Rename project', project.name);
        if (next && next.trim() && next.trim() !== project.name) {
          project.name = next.trim();
          save(state);
          renderProjects();
          refreshProjectSelects();
          toast('Project renamed');
        }
        return;
      }
      if (e.target.matches('[data-delete]')) {
        if (confirm('Delete project "' + project.name + '"? This cannot be undone.')) {
          state.projects.splice(idx, 1);
          save(state);
          renderProjects();
          refreshProjectSelects();
          toast('Project deleted');
        }
        return;
      }
      if (e.target.matches('[data-add-to-cart]')) {
        addProjectToCart(project, e.target);
        return;
      }
    });
  }

  async function addProjectToCart(project, btn) {
    if (!project.items || !project.items.length) return;
    const original = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Adding…';
    try {
      const payload = {
        items: project.items.map((it) => ({ id: it.variant_id, quantity: it.qty || 1 }))
      };
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || ('HTTP ' + res.status));
      }
      const data = await res.json();
      const added = (data && data.items) ? data.items.length : project.items.length;
      toast(added + ' item' + (added === 1 ? '' : 's') + ' added to cart');
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      setTimeout(() => { window.location.href = '/cart'; }, 900);
    } catch (e) {
      console.error('[trade] add to cart failed', e);
      toast('Could not add project to cart: ' + e.message);
      btn.disabled = false;
      btn.textContent = original;
    }
  }

  initTabs();
  renderProjects();
})();

/* ===========================================================
   PDP add-to-project widget
   =========================================================== */
(function () {
  'use strict';

  const STORAGE_PREFIX = 'adl_trade_wishlists_';

  function getCustomerKey() {
    const widget = document.querySelector('[data-trade-add-to-project]');
    if (!widget) return null;
    return STORAGE_PREFIX + (widget.getAttribute('data-customer-id') || 'anon');
  }
  function loadState() {
    const key = getCustomerKey();
    if (!key) return { projects: [] };
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return { projects: [] };
      const parsed = JSON.parse(raw);
      return parsed && Array.isArray(parsed.projects) ? parsed : { projects: [] };
    } catch (e) { return { projects: [] }; }
  }
  function saveState(state) {
    const key = getCustomerKey();
    if (!key) return;
    try { localStorage.setItem(key, JSON.stringify(state)); } catch (e) { /* noop */ }
  }

  function widgetToast(msg, state) {
    const el = document.querySelector('[data-trade-add-to-project] .trade-add-to-project__status');
    if (!el) return;
    el.textContent = msg;
    el.setAttribute('data-state', state || '');
    setTimeout(() => {
      if (el.textContent === msg) {
        el.textContent = '';
        el.removeAttribute('data-state');
      }
    }, 3500);
  }

  function init() {
    const widget = document.querySelector('[data-trade-add-to-project]');
    if (!widget) return;
    const btn = widget.querySelector('.trade-add-to-project__btn');
    const select = widget.querySelector('select[name="project"]');
    const newInput = widget.querySelector('input[name="new-project"]');
    const confirmBtn = widget.querySelector('[data-confirm]');

    function refreshSelect() {
      const state = loadState();
      const opts = state.projects.map((p) =>
        `<option value="${p.name.replace(/"/g, '&quot;')}">${p.name.replace(/</g, '&lt;')}</option>`
      ).join('');
      select.innerHTML = opts + '<option value="__new__">+ Create a new project…</option>';
      newInput.style.display = state.projects.length === 0 ? 'block' : 'none';
      if (state.projects.length === 0) select.value = '__new__';
    }
    refreshSelect();
    document.addEventListener('trade:projects-changed', refreshSelect);

    btn.addEventListener('click', (e) => {
      e.preventDefault();
      const open = widget.getAttribute('data-open') === 'true';
      widget.setAttribute('data-open', open ? 'false' : 'true');
    });

    select.addEventListener('change', () => {
      newInput.style.display = select.value === '__new__' ? 'block' : 'none';
      if (select.value === '__new__') newInput.focus();
    });

    document.addEventListener('click', (e) => {
      if (!widget.contains(e.target)) widget.setAttribute('data-open', 'false');
    });

    confirmBtn.addEventListener('click', (e) => {
      e.preventDefault();
      let projectName = select.value;
      if (projectName === '__new__') {
        projectName = (newInput.value || '').trim();
        if (!projectName) {
          widgetToast('Please name the project', 'error');
          return;
        }
      }

      const form = document.querySelector('form[action*="/cart/add"]');
      let variantId = widget.getAttribute('data-variant-id');
      let qty = 1;
      if (form) {
        const vInput = form.querySelector('[name="id"]');
        const qInput = form.querySelector('[name="quantity"]');
        if (vInput && vInput.value) variantId = vInput.value;
        if (qInput && qInput.value) qty = parseInt(qInput.value, 10) || 1;
      }
      if (!variantId) {
        widgetToast('Pick a variant first', 'error');
        return;
      }

      const item = {
        product_id: parseInt(widget.getAttribute('data-product-id'), 10) || null,
        variant_id: parseInt(variantId, 10),
        qty: qty,
        title: widget.getAttribute('data-product-title') || '',
        url: widget.getAttribute('data-product-url') || '',
        image: widget.getAttribute('data-product-image') || '',
        variant_title: widget.getAttribute('data-variant-title') || ''
      };

      const state = loadState();
      let project = state.projects.find((p) => p.name === projectName);
      if (!project) {
        project = { name: projectName, items: [] };
        state.projects.push(project);
      }
      const existing = project.items.find((it) => it.variant_id === item.variant_id);
      if (existing) existing.qty = (existing.qty || 1) + item.qty;
      else project.items.push(item);
      saveState(state);

      widgetToast('Added to "' + projectName + '"', 'success');
      newInput.value = '';
      refreshSelect();
      setTimeout(() => widget.setAttribute('data-open', 'false'), 900);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
